'use client';

import { c as _c } from "react/compiler-runtime";
import type { CardAISessionSummary } from '@shared/all/domains/electron';
import type { Factiii } from '@shared/all/domains/factiiis';
import { ModerationReportType } from '@shared/all/domains/moderation';
import { asConfigTab, type ConfigTab } from '@shared/all/domains/runnerConfigTabs';
import { LayoutType, SpaceMemberRoles, Types } from '@shared/all/domains/spaces';
import { type Upload } from '@shared/all/domains/uploads';
import { boardTerminalKey } from '@shared/all/helpers/board-agent-core/boardTerminal';
import { buildChatLink } from '@shared/all/helpers/chatLink';
import { copyToClipboard } from '@shared/utils/helpers/clipboard';
import { DEMO_SLUG } from '@shared/utils/helpers/demoBoardData';
import { humanizeSetupError } from '@shared/utils/helpers/setupError';
import toast from '@shared/utils/helpers/toast';
import useDeployRunning from '@shared/utils/hooks/useDeployRunning';
import useSpace from '@shared/utils/hooks/useSpace';
import useUser from '@shared/utils/hooks/useUser';
import { type InfiniteData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, AlertCircle, Apple, Cpu, Download, ExternalLink, History, Laptop, LayoutGrid, List, MessageCircle, MonitorCog, MoreVertical, Server, Sparkles, Tags } from 'lucide-react';
import dynamic from 'next/dynamic';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { focusTerminalWindow, showTerminalWindow } from '../../helpers/terminalWindow';
import { useBoardRunnerAttention } from '../../hooks/useBoardRunnerAttention';
import useClickOutside from '../../hooks/useClickOutside';
import { useDetachedTerminal } from '../../hooks/useDetachedTerminal';
import { useHeaderHeight } from '../../hooks/useHeaderHeight';
import useIsElectron from '../../hooks/useIsElectron';
import { useRunner } from '../../hooks/useRunnerContext';
import { client, type RouterOutputs } from '../../utils/trpc';
import { useAppModals, useAuthModals } from '../../widgets/WidgetsContext';
import Avatar from '../common/Avatar';
import { AttentionDot } from '../pages/space/runnerSelector/RunnerAttentionList';
import Button from '../common/Button';
import PageBanner, { BannerAction, BannerChip, BannerMenu, BannerStat } from '../common/PageBanner';
import PinnedFactiiis from '../common/PinnedFactiiis';
import FactiiisList from '../factiiis/FactiiisList';
import Modal, { ModalContent, ModalHeader } from '../HOC/Modal';
import BanStatsButton from './BanStats';
import EmbedSpaceModal from './EmbedSpaceModal';
import HeaderBoards from './HeaderBoards';
import InviteMembersModal from './InviteMembersModal';
import RemovalStatsButton from './RemovalStats';
import SpaceDropdown from './SpaceDropdown';
import SpaceEdit from './SpaceEdit';
import SpaceMembersList from './SpaceMembersList';
import TrelloImportModal from './TrelloImportModal';
const BoardAIModal = dynamic(() => import('../pages/space/BoardAIModal'), {
  ssr: false
});
const CardAISessionModal = dynamic(() => import('../pages/space/CardAIModal'), {
  ssr: false
});
const BareCardSessionModal = dynamic(() => import('../pages/space/BareCardModal'), {
  ssr: false
});

// Dynamic also breaks the import cycle: the demo renders this header
const DevelopmentDemo = dynamic(() => import('../onboarding/DevelopmentDemo'), {
  ssr: false
});
type Props = {
  uploads?: Upload[] | undefined;
  showSubscribeModalHandler?: () => void;
  space: RouterOutputs['spaces']['get'];
  isHomePage?: boolean;
  isEmbed: boolean;
  // Effective board layout including the client-side `?view=board` override;
  // falls back to the space's stored layout when not provided.
  isBoardLayout?: boolean;
  onHistoryClick?: () => void;
  // When true, the header collapses its top margin because another element
  // (like the invite banner) is already clearing the fixed top bar above it.
  hasBanner?: boolean;
};
const Header = props => {
  const $ = _c(126);
  const {
    currentUser
  } = useUser();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["users", "spaceFilters"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = currentUser.isAuthenticated === true;
  let t2;
  if ($[1] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: _temp,
      enabled: t1,
      staleTime: Infinity
    };
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    data: t3
  } = useQuery(t2);
  let t4;
  if ($[3] !== t3) {
    t4 = t3 === undefined ? [] : t3;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const spaceFilterIds = t4;
  const {
    openAuthContinueModal
  } = useAuthModals();
  const {
    newModerationReport
  } = useAppModals();
  const {
    isOwner,
    joinSpaceUpdate,
    leaveSpaceUpdate
  } = useSpace();
  const [factiiiModalMode, setFactiiiModalMode] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [showMembersListModal, setShowMembersListModal] = useState(false);
  const [profileOptionsDropdownOpen, setProfileOptionsDropdownOpen] = useState(false);
  const [inviteMembersModalOpen, setInviteMembersModalOpen] = useState(false);
  const [notificationDismissed, setNotificationDismissed] = useState(false);
  const [embedSpaceModalOpen, setEmbedSpaceModalOpen] = useState(false);
  const [trelloImportModalOpen, setTrelloImportModalOpen] = useState(false);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {};
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const [aiSessions, setAiSessions] = useState(t5);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      postId: null,
      forceBare: false
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const [aiModal, setAiModal] = useState(t6);
  const [boardAIOpen, setBoardAIOpen] = useState(false);
  const [terminalWindow, setTerminalWindow] = useState(null);
  const detached = useDetachedTerminal();
  const [boardTourOpen, setBoardTourOpen] = useState(false);
  const isElectron = useIsElectron();
  const headerClearance = useHeaderHeight();
  const {
    runners,
    selectedRunner,
    agent: boardAgent,
    config: runnerConfig
  } = useRunner();
  const runnerSetup = runnerConfig.setup;
  const runnerAttention = useBoardRunnerAttention();
  const runnerUnhealthy = runnerAttention.level === "broken";
  let t7;
  if ($[7] !== selectedRunner) {
    t7 = r => r.socketId === selectedRunner;
    $[7] = selectedRunner;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const activeRunner = runners.find(t7);
  const queryClient = useQueryClient();
  const dropdownRef = useRef(null);
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setProfileOptionsDropdownOpen(false);
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  useClickOutside(dropdownRef, t8);
  let t9;
  if ($[10] !== props.space.id || $[11] !== spaceFilterIds) {
    t9 = spaceFilterIds.includes(props.space.id);
    $[10] = props.space.id;
    $[11] = spaceFilterIds;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const filtered = t9;
  const spaceMembership = props.space.membership;
  let t10;
  if ($[13] !== spaceMembership?.roles) {
    t10 = spaceMembership?.roles.includes(SpaceMemberRoles.INVITED) || false;
    $[13] = spaceMembership?.roles;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const isInvitedRole = t10;
  let t11;
  if ($[15] !== isInvitedRole || $[16] !== props.space.types || $[17] !== spaceMembership) {
    t11 = !spaceMembership && !props.space.types.includes(Types.INVITE) || isInvitedRole;
    $[15] = isInvitedRole;
    $[16] = props.space.types;
    $[17] = spaceMembership;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const canJoin = t11;
  let t12;
  if ($[19] !== isOwner || $[20] !== props.space || $[21] !== spaceMembership) {
    t12 = spaceMembership && spaceMembership.roles.includes(SpaceMemberRoles.MEMBER) && !isOwner(props.space);
    $[19] = isOwner;
    $[20] = props.space;
    $[21] = spaceMembership;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const canLeave = t12;
  const spaceSlug = props.space.spaceSlug;
  const defaultsToBoard = props.space?.config?.defaultLayout === LayoutType.Board;
  const isBoardLayout = props.isBoardLayout ?? defaultsToBoard;
  const canToggleView = !props.isHomePage;
  let t13;
  let t14;
  if ($[23] !== boardAgent) {
    t13 = () => {
      if (!boardAgent) {
        return;
      }
      let ignore = false;
      boardAgent.cardSessions().then(sessions => {
        if (!ignore) {
          setAiSessions(sessions);
        }
      }).catch(_temp2);
      const unsub = boardAgent.onSessionUpdate(data => {
        setAiSessions(prev => {
          const next = {
            ...prev
          };
          if (!data.phase) {
            delete next[data.postId];
          } else {
            next[data.postId] = data;
          }
          return next;
        });
      });
      return () => {
        ignore = true;
        unsub();
      };
    };
    t14 = [boardAgent];
    $[23] = boardAgent;
    $[24] = t13;
    $[25] = t14;
  } else {
    t13 = $[24];
    t14 = $[25];
  }
  useEffect(t13, t14);
  let t15;
  if ($[26] !== aiSessions) {
    t15 = Object.values(aiSessions).filter(_temp3);
    $[26] = aiSessions;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const sessionCount = t15.length;
  const deployRun = useDeployRunning(boardAgent);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t16;
  if ($[28] !== searchParams) {
    t16 = searchParams?.get("card") ?? null;
    $[28] = searchParams;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const deepCard = t16;
  const bareDeepLinkId = deepCard && aiSessions[deepCard]?.mode === "bare" ? deepCard : null;
  const shownPostId = aiModal.postId ?? bareDeepLinkId;
  let t17;
  if ($[30] !== bareDeepLinkId || $[31] !== pathname || $[32] !== router) {
    t17 = () => {
      setAiModal({
        postId: null,
        forceBare: false
      });
      if (bareDeepLinkId && pathname) {
        router.replace(pathname);
      }
    };
    $[30] = bareDeepLinkId;
    $[31] = pathname;
    $[32] = router;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  const closeSessionModal = t17;
  let t18;
  if ($[34] !== searchParams) {
    t18 = asConfigTab(searchParams?.get("runnerTab"));
    $[34] = searchParams;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const runnerDeepTab = t18;
  let t19;
  if ($[36] !== runnerDeepTab || $[37] !== terminalWindow) {
    t19 = terminalWindow ?? (runnerDeepTab ? {
      tab: runnerDeepTab
    } : null);
    $[36] = runnerDeepTab;
    $[37] = terminalWindow;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const shownTerminalWindow = t19;
  let t20;
  if ($[39] !== pathname || $[40] !== router || $[41] !== runnerDeepTab) {
    t20 = () => {
      setTerminalWindow(null);
      if (runnerDeepTab && pathname) {
        router.replace(pathname);
      }
    };
    $[39] = pathname;
    $[40] = router;
    $[41] = runnerDeepTab;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  const closeTerminalWindow = t20;
  let t21;
  if ($[43] !== detached || $[44] !== spaceSlug) {
    t21 = tab => detached ? showTerminalWindow({
      spaceSlug,
      tab
    }) : setTerminalWindow({
      tab
    });
    $[43] = detached;
    $[44] = spaceSlug;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  const openRunnerOn = t21;
  let t22;
  if ($[46] !== defaultsToBoard || $[47] !== pathname || $[48] !== router || $[49] !== searchParams) {
    t22 = board => {
      const params = new URLSearchParams(searchParams?.toString());
      if (board === defaultsToBoard) {
        params.delete("view");
      } else {
        params.set("view", board ? "board" : "space");
      }
      const qs = params.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname ?? "", {
        scroll: false
      });
    };
    $[46] = defaultsToBoard;
    $[47] = pathname;
    $[48] = router;
    $[49] = searchParams;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  const setBoardView = t22;
  let t23;
  if ($[51] !== currentUser.isAuthenticated || $[52] !== isInvitedRole || $[53] !== joinSpaceUpdate || $[54] !== openAuthContinueModal || $[55] !== props || $[56] !== spaceSlug) {
    t23 = async () => {
      if (!currentUser.isAuthenticated) {
        return openAuthContinueModal();
      }
      if (props.space.types.includes(Types.PAID)) {
        if (props.showSubscribeModalHandler) {
          props.showSubscribeModalHandler();
        }
      } else {
        if (!props.space.types.includes(Types.PRIVATE) || isInvitedRole) {
          const [error, data_0] = await client.spaces.join.mutate({
            slug: spaceSlug
          });
          if (error) {
            return;
          }
          joinSpaceUpdate(spaceSlug, data_0);
        }
      }
    };
    $[51] = currentUser.isAuthenticated;
    $[52] = isInvitedRole;
    $[53] = joinSpaceUpdate;
    $[54] = openAuthContinueModal;
    $[55] = props;
    $[56] = spaceSlug;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  const joinHandler = t23;
  let t24;
  if ($[58] !== currentUser.isAuthenticated || $[59] !== isOwner || $[60] !== leaveSpaceUpdate || $[61] !== openAuthContinueModal || $[62] !== props.space || $[63] !== spaceSlug) {
    t24 = async () => {
      if (!currentUser.isAuthenticated) {
        return openAuthContinueModal();
      }
      if (isOwner(props.space)) {
        return;
      }
      if (!props.space.types.includes(Types.PAID)) {
        const [error_0] = await client.spaces.leave.mutate({
          slug: spaceSlug
        });
        if (error_0) {
          return;
        }
        leaveSpaceUpdate(spaceSlug);
      } else {
        if (props.space.types.includes(Types.PAID)) {
          const conf = confirm("Your subscription will be cancelled and you'll not be able to post to this Space.");
          if (!conf) {
            return;
          }
          const [error_1] = await client.spaces.cancelSubscription.mutate({
            slug: spaceSlug
          });
          if (error_1) {
            return;
          }
          leaveSpaceUpdate(spaceSlug);
        }
      }
    };
    $[58] = currentUser.isAuthenticated;
    $[59] = isOwner;
    $[60] = leaveSpaceUpdate;
    $[61] = openAuthContinueModal;
    $[62] = props.space;
    $[63] = spaceSlug;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  const leaveHandler = t24;
  let t25;
  if ($[65] !== spaceSlug) {
    t25 = async () => {
      await copyToClipboard(`${process.env.NEXT_PUBLIC_SITE_URL}/s/${spaceSlug}`);
      toast("Space link copied to clipboard");
      setProfileOptionsDropdownOpen(false);
    };
    $[65] = spaceSlug;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  const copyLinkHandler = t25;
  let t26;
  if ($[67] !== queryClient || $[68] !== spaceSlug) {
    t26 = async type => {
      if (type === "add") {
        await client.spaces.addFilter.mutate({
          slug: spaceSlug
        });
      } else {
        if (type === "remove") {
          await client.spaces.removeFilter.mutate({
            slug: spaceSlug
          });
        }
      }
      await queryClient.invalidateQueries({
        queryKey: ["users", "spaceFilters"]
      });
      setProfileOptionsDropdownOpen(false);
    };
    $[67] = queryClient;
    $[68] = spaceSlug;
    $[69] = t26;
  } else {
    t26 = $[69];
  }
  const filterChangeHandler = t26;
  let t27;
  if ($[70] !== currentUser.isAuthenticated || $[71] !== newModerationReport || $[72] !== openAuthContinueModal || $[73] !== spaceSlug) {
    t27 = () => {
      if (!currentUser.isAuthenticated) {
        return openAuthContinueModal();
      }
      newModerationReport(ModerationReportType.SPACE, spaceSlug);
      setProfileOptionsDropdownOpen(false);
    };
    $[70] = currentUser.isAuthenticated;
    $[71] = newModerationReport;
    $[72] = openAuthContinueModal;
    $[73] = spaceSlug;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  const reportClickHandler = t27;
  const t28 = "flex";
  const t29 = `${props.hasBanner ? "" : "mt-2"} w-full`;
  const t30 = isBoardLayout ? <div style={props.hasBanner ? undefined : {
    marginTop: headerClearance
  }} className="relative z-20 mb-3 flex w-full flex-col items-stretch gap-2 rounded-xl border border-ds-border-subtle bg-background-secondary/60 px-3 py-2 shadow-sm backdrop-blur-xl sm:mb-4 sm:px-4 laptop:flex-row laptop:items-center laptop:gap-4"><div className="flex min-w-0 items-center gap-2.5"><Avatar spaceAvatarKey={props.space.avatarKey} size={36} className="shrink-0 rounded-lg" /><div className="flex min-w-0 flex-col leading-tight"><div className="flex items-center gap-1.5"><h1 className="truncate text-base font-bold text-textPrimary sm:text-lg">{props.space.name}</h1>{props.space.types.includes(Types.PRIVATE) && <span className="shrink-0 rounded border border-destructive/20 bg-destructive/10 px-1.5 py-0.5 text-[10px] font-emphasis tracking-wide text-destructive">PRIVATE</span>}</div><button onClick={() => setShowMembersListModal(true)} className="mt-0.5 self-start text-xs text-textSecondary transition-colors hover:text-textPrimary sm:text-sm">{props.space.membersCount?.toLocaleString() ?? 0} members</button></div></div><div className="flex min-w-0 flex-wrap items-start gap-2 laptop:contents"><div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">{(() => {
          const p = activeRunner?.platform?.toLowerCase() ?? "";
          const localSelected = isElectron && !selectedRunner;
          const hasAny = isElectron || runners.length > 0;
          const Icon = localSelected ? Laptop : p === "darwin" ? Apple : p === "win32" ? MonitorCog : p === "linux" ? Server : activeRunner ? Laptop : Cpu;
          const runnerName = activeRunner?.name ?? (localSelected ? "Local" : hasAny ? "Select runner" : "Set up runner");
          const sessionsRunning = sessionCount > 0 ? `${runnerName}, ${sessionCount} session${sessionCount === 1 ? "" : "s"} running` : null;
          let runnerLabel = sessionsRunning ?? runnerName;
          if (deployRun.running) {
            runnerLabel = `${runnerLabel}, ${deployRun.blocked ? "deploy needs you" : "deploying"}`;
          }
          let runnerTitle = sessionsRunning ?? "Runner settings and terminal sessions";
          if (detached) {
            runnerTitle = "Your terminal is open in its own window \u2014 bring it to the front";
          }
          if (runnerAttention.items.length > 0) {
            runnerTitle = runnerAttention.items.map(_temp4).join("\n");
          }
          if (runnerSetup.phase === "error") {
            runnerTitle = humanizeSetupError(runnerSetup.error);
          }
          if (deployRun.running) {
            runnerTitle = `${deployRun.blocked ? "Deploy needs you" : "Deploying"}: click for its console\n${runnerTitle}`;
          }
          let runnerTone = "text-textSecondary hover:bg-hover hover:text-textPrimary";
          if (sessionCount > 0 || deployRun.running) {
            runnerTone = "text-primary hover:bg-primary/10";
          }
          if (runnerUnhealthy) {
            runnerTone = "bg-destructive/10 text-destructive hover:bg-destructive/20";
          }
          return <button data-id="board-runner" onClick={() => {
            if (deployRun.running) {
              openRunnerOn("deploy");
            } else {
              if (detached) {
                focusTerminalWindow({
                  spaceSlug
                });
              } else {
                setTerminalWindow({});
              }
            }
          }} aria-label={runnerLabel} title={runnerTitle} className={`flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold transition-colors sm:text-sm ${runnerTone}`}>{runnerUnhealthy ? <AlertCircle size={14} className="shrink-0" /> : <Icon size={14} />}<span className="max-w-[120px] truncate">{runnerName}</span>{(runnerAttention.level === "needs-you" || deployRun.blocked) && <AttentionDot level="needs-you" />}{detached ? <ExternalLink size={14} className="shrink-0" /> : sessionCount > 0 && <span className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-primary px-1 text-xs font-semibold text-white">{sessionCount}</span>}</button>;
        })()}{canToggleView && <button onClick={() => setBoardView(false)} className="flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold text-textSecondary transition-colors hover:bg-hover hover:text-textPrimary sm:text-sm"><List size={14} /><span className="hidden sm:inline">Space view</span></button>}<button data-id="board-ask-ai" onClick={() => setBoardAIOpen(true)} className="flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold text-textSecondary transition-colors hover:bg-hover hover:text-textPrimary sm:text-sm"><Sparkles size={14} /><span className="hidden sm:inline">Ask Board</span></button>{isOwner(props.space) && currentUser.isAuthenticated && <><button onClick={() => setFactiiiModalMode("lists")} className="flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-emphasis text-textSecondary transition-colors hover:bg-background-light hover:text-textPrimary sm:text-sm"><Tags size={14} /><span className="hidden sm:inline">List</span></button><button onClick={() => setTrelloImportModalOpen(true)} className="flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-emphasis text-textSecondary transition-colors hover:bg-background-light hover:text-textPrimary sm:text-sm"><Download size={14} /><span className="hidden sm:inline">Import</span></button></>}{props.onHistoryClick && <button data-id="board-activity" onClick={props.onHistoryClick} className="flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-emphasis text-textSecondary transition-colors hover:bg-background-light hover:text-textPrimary sm:text-sm"><Activity size={14} /><span className="hidden sm:inline">Activity</span></button>}</div><div className="flex shrink-0 items-center gap-1">{currentUser.isAuthenticated && <div className="relative" ref={dropdownRef}><button onClick={() => setProfileOptionsDropdownOpen(_temp5)} title="More options" className="flex h-8 items-center justify-center rounded-lg px-2 text-textSecondary transition-colors hover:bg-background-light hover:text-textPrimary"><MoreVertical size={14} /></button>{profileOptionsDropdownOpen && <div className="absolute right-0 top-full z-50 mt-2 min-w-[220px]"><SpaceDropdown space={props.space} uploads={props.uploads} copyLinkHandler={copyLinkHandler} slug={spaceSlug} loggedIn={currentUser.isAuthenticated} filtered={filtered} filterChangeHandler={filterChangeHandler} reportClickHandler={reportClickHandler} types={props.space.types} isInvited={isInvitedRole} joinHandler={joinHandler} leaveHandler={leaveHandler} closeHandler={() => setProfileOptionsDropdownOpen(false)} openEmbedModal={() => setEmbedSpaceModalOpen(true)} editHandler={() => setEditModalOpen(true)} showHistoryLink={!props.isHomePage} isBoardLayout={true} onTakeTour={spaceSlug === DEMO_SLUG ? undefined : () => setBoardTourOpen(true)} /></div>}</div>}{canJoin && !props.space.types.includes(Types.PAID) && <button onClick={joinHandler} className="flex h-8 items-center rounded-lg bg-primary px-3 text-xs font-semibold text-white transition-colors hover:bg-primary/90 sm:px-4 sm:text-sm">Join</button>}{canLeave && !props.space.types.includes(Types.PAID) && <button onClick={leaveHandler} className="flex h-8 items-center rounded-lg bg-red-600 px-3 text-xs font-semibold text-white transition-colors hover:bg-red-700 sm:px-4 sm:text-sm">Leave</button>}{canLeave && spaceMembership.expires === null && props.space.types.includes(Types.PAID) && <button onClick={leaveHandler} className="flex h-8 items-center rounded-lg bg-red-600 px-3 text-xs font-semibold text-white transition-colors hover:bg-red-700 sm:px-4 sm:text-sm">Cancel</button>}</div></div></div> : <PageBanner variant="space" topOffset={!props.hasBanner} entityKey={props.space.spaceSlug} bannerKey={props.space.bannerKey} bannerAlt={"Header Image for " + props.space.name} avatar={<Avatar key={`${props.space.spaceSlug}-${props.space.avatarKey ?? "default"}`} spaceAvatarKey={props.space.avatarKey} size={100} className="border-4 border-background-secondary" />} coverActions={!props.isEmbed && <>{canToggleView && <Button text="Board view" variant="CLEAR" classNames="px-2.5 py-1 text-xs" icon={<LayoutGrid size={15} />} onClick={() => setBoardView(true)} />}{isOwner(props.space) && currentUser.isAuthenticated && <><Button text="Edit Space" variant="CLEAR" classNames="px-2.5 py-1 text-xs" onClick={() => setEditModalOpen(true)} /><Button text="Manage Factiiis" variant="CLEAR" classNames="px-2.5 py-1 text-xs" onClick={() => setFactiiiModalMode("all")} /></>}{props.space.spaceSlug && <><BanStatsButton spaceSlug={props.space.spaceSlug} count={props.space.banned_count} /><RemovalStatsButton spaceSlug={props.space.spaceSlug} /></>}</>} title={props.space.name} subtitle={props.space.types.includes(Types.PRIVATE) && <BannerChip text="PRIVATE" tone="danger" />} description={props.space.description} stats={!props.isEmbed && !props.isHomePage && Boolean(props.space.membersCount) && <BannerStat label="Members" value={props.space.membersCount?.toLocaleString()} onClick={() => setShowMembersListModal(true)} />} actions={!props.isEmbed && currentUser.isAuthenticated !== null && spaceSlug !== "" && !props.isHomePage && <><BannerAction href={buildChatLink({
      spaceSlug
    })} icon={<MessageCircle size={15} />} text="Chat" /><BannerAction href={`/s/${spaceSlug}/history`} icon={<History size={15} />} text="History" />{canJoin && !props.space.types.includes(Types.PAID) && <Button text="Join Space" onClick={joinHandler} />}{canLeave && <Button text="Leave Space" variant="DANGER" disabled={isOwner(props.space)} onClick={leaveHandler} />}{canLeave && spaceMembership.expires === null && props.space.types.includes(Types.PAID) && <Button text="Cancel Membership" variant="DANGER" disabled={isOwner(props.space)} onClick={leaveHandler} />}{canLeave && spaceMembership.expires !== null && new Date(spaceMembership.expires ?? 0) >= new Date() && props.space.types.includes(Types.PAID) && <Button text={`Expires on ${new Date(spaceMembership.expires ?? 0).toLocaleDateString()}`} disabled={true} />}{currentUser.isAuthenticated && <BannerMenu label="Space Options Dropdown" menuRef={dropdownRef} open={profileOptionsDropdownOpen} onToggle={() => setProfileOptionsDropdownOpen(_temp6)}><div className="absolute right-0 top-full z-50 mt-2 min-w-[220px]"><SpaceDropdown space={props.space} uploads={props.uploads} copyLinkHandler={copyLinkHandler} slug={spaceSlug} loggedIn={currentUser.isAuthenticated} filtered={filtered} filterChangeHandler={filterChangeHandler} reportClickHandler={reportClickHandler} types={props.space.types} isInvited={isInvitedRole} joinHandler={joinHandler} leaveHandler={leaveHandler} closeHandler={() => setProfileOptionsDropdownOpen(false)} openEmbedModal={() => setEmbedSpaceModalOpen(true)} editHandler={() => setEditModalOpen(true)} showHistoryLink={!props.isHomePage} /></div></BannerMenu>}</>}>{!props.isEmbed && !notificationDismissed && !spaceMembership && isInvitedRole && <div className="mx-4 flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-sm" role="alert"><p className="min-w-0 flex-1">You've been invited to join this invite-only space.{" "}<span onClick={joinHandler} className="cursor-pointer text-primary underline">Join now!</span></p><button type="button" aria-label="Close" onClick={() => setNotificationDismissed(true)} className="shrink-0 px-1 text-textSecondary hover:text-textPrimary"><span>×</span></button></div>}{props.space.pinnedFactiiis && props.space.pinnedFactiiis.length > 0 && <PinnedFactiiis spaceSlug={spaceSlug} factiiis={props.space.pinnedFactiiis} />}{props.space.headerBoards && props.space.headerBoards.length > 0 && <HeaderBoards boards={props.space.headerBoards} />}</PageBanner>;
  let t31;
  if ($[75] !== editModalOpen || $[76] !== props.space) {
    t31 = editModalOpen && <SpaceEdit closeHandler={() => setEditModalOpen(false)} pinnedFactiiis={props.space.pinnedFactiiis || []} space={props.space} />;
    $[75] = editModalOpen;
    $[76] = props.space;
    $[77] = t31;
  } else {
    t31 = $[77];
  }
  let t32;
  if ($[78] !== factiiiModalMode || $[79] !== isOwner || $[80] !== props.space) {
    t32 = factiiiModalMode && "spaceSlug" in props.space && <Modal closeHandler={() => setFactiiiModalMode(null)}><ModalHeader closeHandler={() => setFactiiiModalMode(null)}><h2>{factiiiModalMode === "lists" ? `Lists on ${props.space.spaceSlug}` : isOwner(props.space) ? `Factiiis for ${props.space.spaceSlug}` : "Factiiis for you on this space"}</h2></ModalHeader><ModalContent><FactiiisList space={props.space} slug={props.space.spaceSlug} onlyLists={factiiiModalMode === "lists"} /></ModalContent></Modal>;
    $[78] = factiiiModalMode;
    $[79] = isOwner;
    $[80] = props.space;
    $[81] = t32;
  } else {
    t32 = $[81];
  }
  let t33;
  if ($[82] !== isOwner || $[83] !== props.space || $[84] !== showMembersListModal || $[85] !== spaceSlug) {
    t33 = showMembersListModal && <Modal closeHandler={() => setShowMembersListModal(false)}><ModalHeader closeHandler={() => setShowMembersListModal(false)}><div className="flex flex-1 items-center justify-between"><h1>Space Members</h1>{isOwner(props.space) && <Button text="Invite User" classNames="text-sm mr-2" variant="SECONDARY" onClick={() => {
            setShowMembersListModal(false);
            setInviteMembersModalOpen(true);
          }} />}</div></ModalHeader><ModalContent><SpaceMembersList slug={spaceSlug} space={props.space} /></ModalContent></Modal>;
    $[82] = isOwner;
    $[83] = props.space;
    $[84] = showMembersListModal;
    $[85] = spaceSlug;
    $[86] = t33;
  } else {
    t33 = $[86];
  }
  let t34;
  if ($[87] !== inviteMembersModalOpen || $[88] !== spaceSlug) {
    t34 = inviteMembersModalOpen && <InviteMembersModal closeHandler={() => setInviteMembersModalOpen(false)} slug={spaceSlug} />;
    $[87] = inviteMembersModalOpen;
    $[88] = spaceSlug;
    $[89] = t34;
  } else {
    t34 = $[89];
  }
  let t35;
  if ($[90] !== embedSpaceModalOpen || $[91] !== spaceSlug) {
    t35 = embedSpaceModalOpen && <EmbedSpaceModal closeHandler={() => setEmbedSpaceModalOpen(false)} slug={spaceSlug} />;
    $[90] = embedSpaceModalOpen;
    $[91] = spaceSlug;
    $[92] = t35;
  } else {
    t35 = $[92];
  }
  let t36;
  if ($[93] !== spaceSlug || $[94] !== trelloImportModalOpen) {
    t36 = trelloImportModalOpen && <TrelloImportModal closeHandler={() => setTrelloImportModalOpen(false)} spaceSlug={spaceSlug} onImportComplete={_temp7} />;
    $[93] = spaceSlug;
    $[94] = trelloImportModalOpen;
    $[95] = t36;
  } else {
    t36 = $[95];
  }
  let t37;
  if ($[96] !== boardTourOpen) {
    t37 = boardTourOpen && <DevelopmentDemo onClose={() => setBoardTourOpen(false)} />;
    $[96] = boardTourOpen;
    $[97] = t37;
  } else {
    t37 = $[97];
  }
  let t38;
  if ($[98] !== boardAIOpen || $[99] !== queryClient || $[100] !== spaceSlug) {
    t38 = boardAIOpen && <BoardAIModal closeHandler={() => setBoardAIOpen(false)} factiiis={queryClient.getQueryData(["boardFactiiis", spaceSlug])?.pages.flatMap(_temp8) ?? []} spaceSlug={spaceSlug} onOpenTerminal={() => {
      setBoardAIOpen(false);
      setAiModal({
        postId: boardTerminalKey(spaceSlug),
        forceBare: true
      });
    }} />;
    $[98] = boardAIOpen;
    $[99] = queryClient;
    $[100] = spaceSlug;
    $[101] = t38;
  } else {
    t38 = $[101];
  }
  let t39;
  if ($[102] !== closeTerminalWindow || $[103] !== shownPostId || $[104] !== shownTerminalWindow || $[105] !== spaceSlug) {
    t39 = shownTerminalWindow && !shownPostId && <BareCardSessionModal spaceSlug={spaceSlug} configTab={shownTerminalWindow.tab} closeHandler={closeTerminalWindow} />;
    $[102] = closeTerminalWindow;
    $[103] = shownPostId;
    $[104] = shownTerminalWindow;
    $[105] = spaceSlug;
    $[106] = t39;
  } else {
    t39 = $[106];
  }
  let t40;
  if ($[107] !== aiModal.forceBare || $[108] !== aiSessions || $[109] !== closeSessionModal || $[110] !== shownPostId || $[111] !== spaceSlug) {
    t40 = shownPostId && aiSessions[shownPostId] && (aiSessions[shownPostId].mode === "bare" || aiModal.forceBare ? <BareCardSessionModal post={{
      id: shownPostId,
      title: aiSessions[shownPostId].taskTitle || "",
      content: ""
    }} spaceSlug={spaceSlug} closeHandler={closeSessionModal} /> : <CardAISessionModal post={{
      id: shownPostId,
      title: aiSessions[shownPostId].taskTitle || "",
      content: "",
      contentRows: 1
    }} onOpenTerminal={() => setAiModal({
      postId: shownPostId,
      forceBare: true
    })} closeHandler={closeSessionModal} />);
    $[107] = aiModal.forceBare;
    $[108] = aiSessions;
    $[109] = closeSessionModal;
    $[110] = shownPostId;
    $[111] = spaceSlug;
    $[112] = t40;
  } else {
    t40 = $[112];
  }
  let t41;
  if ($[113] !== t29 || $[114] !== t30 || $[115] !== t31 || $[116] !== t32 || $[117] !== t33 || $[118] !== t34 || $[119] !== t35 || $[120] !== t36 || $[121] !== t37 || $[122] !== t38 || $[123] !== t39 || $[124] !== t40) {
    t41 = <div className={t28}><div className={t29}>{t30}{t31}{t32}{t33}{t34}{t35}{t36}{t37}{t38}{t39}{t40}</div></div>;
    $[113] = t29;
    $[114] = t30;
    $[115] = t31;
    $[116] = t32;
    $[117] = t33;
    $[118] = t34;
    $[119] = t35;
    $[120] = t36;
    $[121] = t37;
    $[122] = t38;
    $[123] = t39;
    $[124] = t40;
    $[125] = t41;
  } else {
    t41 = $[125];
  }
  return t41;
};
export default Header;
async function _temp() {
  const [, d] = await client.users.spaceFilters.query();
  return d?.spaceIds ?? [];
}
function _temp2() {}
function _temp3(s) {
  return s.phase && s.phase !== "error";
}
function _temp4(i) {
  return i.text;
}
function _temp5(prev_0) {
  return !prev_0;
}
function _temp6(prev_1) {
  return !prev_1;
}
function _temp7() {
  window.location.reload();
}
function _temp8(p_0) {
  return p_0.factiiis;
}